import type { ExperimentConfig } from '@/types';

const experiment6Config: ExperimentConfig = {
  id: 'exp6',
  number: 6,
  name: 'Vitamin C Analysis',
  description: 'Determine ascorbic acid (Vitamin C) content in commercial tablets using the USP iodometric titration method with 0.10 N iodine and starch indicator. Verify if tablets meet USP standards (90-110% of labeled amount). Completed individually, no formal report required.',
  estimatedTime: '120 minutes',
  learningObjectives: [
    'Understand iodometric redox titration (ascorbic acid + I₂ → dehydroascorbic acid + 2HI)',
    'Use the starch-iodine indicator system (blue-black complex at endpoint)',
    'Distinguish between rapid titration and dropwise titration near endpoint',
    'Recognize the "dirty purple" endpoint color that persists for more than 90 seconds',
    'Weigh 10 tablets for average mass, grind 3-5 tablets, weigh four ~400 mg samples',
    'Dissolve each sample in 100 mL oxygen-free (boiled) water + 25 mL 2 N H₂SO₄',
    'Add 3 mL starch test solution as endpoint is approached',
    'Perform one blank titration plus three sample titrations (rapid titration, sample 1, 2, 3)',
    'Calculate: each mL of 0.1 N iodine = 8.806 mg C₆H₈O₆',
    'Compare experimental results to USP standards (90-110% of labeled amount)',
  ],
  safetyNotes: [
    'Iodine (0.10 N I₂) solution stains skin and clothing',
    '2 N H₂SO₄ is corrosive - handle with care',
    'Use freshly boiled and cooled water (oxygen-free) to prevent vitamin C oxidation',
    'Starch test solution should be available',
  ],
  requiredEquipment: [
    'Burette (50 mL)',
    'Erlenmeyer flasks (4, labelled: rapid titration, sample 1, 2, 3)',
    'Mortar and pestle',
    'Analytical balance (to 0.0001 g)',
    'Timer (90-second validation)',
    '0.10 N Iodine (I₂) solution',
    '2 N sulphuric acid (25 mL per flask)',
    'Starch test solution (3 mL per flask)',
    'Oxygen-free (boiled) water (100 mL per flask)',
    'Vitamin C tablets (record all relevant information from the bottle)',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'samplePrep',
      title: 'Part I: Sample Preparation',
      equipment: ['Analytical balance', 'Mortar and pestle'],
      initialState: {
        tabletsWeighed: 0,
        totalMass: null,
        avgTabletMass: null,
        tabletsGround: 0,
        grindingProgress: 0,
        samples: [
          { id: 1, mass: null, prepared: false },
          { id: 2, mass: null, prepared: false },
          { id: 3, mass: null, prepared: false },
          { id: 4, mass: null, prepared: false }
        ]
      },
      steps: [
        {
          id: 1,
          title: 'Record Tablet Info and Weigh 10 Tablets',
          shortDescription: 'Record all info from bottle; weigh 10 tablets to nearest 0.0001 g',
          handler: 'handleWeighTablets',
          infoContent: {
            title: 'Average Mass Determination',
            content: 'Record all relevant information from the tablet bottle. Weigh and record to the nearest **0.0001 g**, **10 Vitamin C tablets**. Determine the average weight of a single tablet.',
            whatHappens: 'Ten Vitamin C tablets are weighed together on an analytical balance to determine the average tablet mass, which is needed to convert per-sample results to per-tablet results.',
            observe: [
              'Record all label information from the bottle (brand, mg per tablet, lot number)',
              'Weigh all 10 tablets together and record to 0.0001 g precision',
              'Divide total mass by 10 to calculate average tablet mass',
            ],
          }
        },
        {
          id: 2,
          title: 'Calculate Average Mass',
          shortDescription: 'Divide total mass by 10 to get average',
          handler: 'handleCalculateAverage',
          validation: {
            requires: ['totalMass']
          }
        },
        {
          id: 3,
          title: 'Grind 3-5 Tablets',
          shortDescription: 'Use mortar and pestle to grind 3-5 tablets into fine powder',
          handler: 'handleGrindTablets',
          infoContent: {
            title: 'Sample Homogenization',
            content: 'With a mortar and pestle, grind up **3-5 tablets**. Grinding ensures uniform distribution of vitamin C throughout the sample. Fine powder dissolves more readily.',
            whatHappens: 'Tablets are ground into a fine, homogeneous powder so that each weighed sample contains the same proportion of active ingredient and binder/filler materials.',
            observe: [
              'Grind 3-5 tablets thoroughly until no large pieces remain',
              'The powder should be fine and uniform in texture',
              'A fine powder dissolves much more readily than tablet chunks',
            ],
          }
        },
        {
          id: 4,
          title: 'Weigh Four ~400 mg Samples',
          shortDescription: 'Accurately weigh four samples of about 400 mg each, record exact weights',
          handler: 'handleWeighSample',
          validation: {
            requires: ['tabletsGround']
          },
          infoContent: {
            title: 'Sample Weighing',
            content: 'Accurately weigh **four samples** of about **400 mg** each. Record the exact weight of each sample. Quantitatively transfer the samples to four pre-labelled Erlenmeyer flasks as **rapid titration, sample 1, 2, and 3**.',
            whatHappens: 'Four separate ~400 mg samples of the ground tablet powder are accurately weighed and transferred to labelled flasks for individual titration.',
            observe: [
              'Weigh each sample to 0.0001 g precision and record the exact mass',
              'Transfer each sample quantitatively to its labelled flask (rapid, 1, 2, 3)',
              'Ensure no powder is lost during transfer — rinse weighing vessel if needed',
            ],
          }
        }
      ]
    },
    {
      id: 2,
      name: 'titration',
      title: 'Part II: Iodometric Titration',
      equipment: ['Burette', 'Erlenmeyer flask', 'Magnetic stirrer', 'Timer'],
      initialState: {
        currentSample: 1,
        burette: {
          initialVolume: 0,
          currentVolume: 0
        },
        flask: {
          hasWater: false,
          hasAcid: false,
          hasSample: false,
          hasStarch: false,
          color: '#FFFDE7'
        },
        timer: {
          isRunning: false,
          secondsRemaining: 90,
          colorPersisted: false
        },
        results: [
          { sample: 1, volumeI2: null, mgVitC: null, perTablet: null },
          { sample: 2, volumeI2: null, mgVitC: null, perTablet: null },
          { sample: 3, volumeI2: null, mgVitC: null, perTablet: null },
          { sample: 4, volumeI2: null, mgVitC: null, perTablet: null }
        ]
      },
      steps: [
        {
          id: 1,
          title: 'Transfer Sample to Flask',
          shortDescription: 'Add weighed sample to 250 mL flask',
          handler: 'handleTransferSample'
        },
        {
          id: 2,
          title: 'Add O₂-Free Water',
          shortDescription: 'Add 100 mL of oxygen-free (boiled) water',
          handler: 'handleAddWater',
          validation: {
            requires: ['hasSample']
          },
          infoContent: {
            title: 'Oxygen-Free Water',
            content: 'Dissolve the sample in a mixture of **100 mL of oxygen-free (boiled) water**. Oxygen in water can oxidize Vitamin C, giving falsely low results. We use freshly boiled, cooled water to minimize dissolved O₂.',
            whatHappens: 'Freshly boiled and cooled water (oxygen-free) is used to dissolve the sample, preventing atmospheric oxygen from prematurely oxidizing the ascorbic acid.',
            observe: [
              'Use water that has been freshly boiled and cooled — not regular tap or DI water',
              'The ground tablet powder should dissolve, though some insoluble binder may remain',
              'The solution should appear slightly cloudy due to tablet excipients',
            ],
          }
        },
        {
          id: 3,
          title: 'Add Sulphuric Acid',
          shortDescription: 'Add 25 mL of 2 N sulphuric acid',
          handler: 'handleAddAcid',
          validation: {
            requires: ['hasWater'],
          },
          infoContent: {
            title: 'Acidification (2 N H₂SO₄)',
            content: 'Add **25 mL of 2 N sulphuric acid**. The acidic environment is necessary for the proper redox reaction between ascorbic acid and iodine. (What is the molarity of the sulphuric acid?)',
            whatHappens: 'Sulfuric acid provides the acidic environment (low pH) necessary for the iodine-ascorbic acid redox reaction to proceed cleanly.',
            observe: [
              'Carefully add 25 mL of 2 N H2SO4 to the flask',
              'The solution may effervesce slightly if the tablet contains carbonate-based binders',
              'No colour change should occur at this stage',
            ],
          }
        },
        {
          id: 4,
          title: 'Fill Burette with I2',
          shortDescription: 'Fill burette with 0.10 N iodine solution',
          handler: 'handleFillBurette',
          validation: {
            requires: ['hasAcid']
          }
        },
        {
          id: 5,
          title: 'Perform Blank Titration First',
          shortDescription: 'Perform one blank titration before starting samples',
          handler: 'handleBlankTitration',
          infoContent: {
            title: 'Blank Titration',
            content: 'Perform **one blank titration** first. Then, starting with the rapid titration flask, titrate each solution at once with **0.10 N iodine solution**.',
            whatHappens: 'A blank titration (water + acid + starch, no vitamin C) determines the volume of iodine consumed by reagents alone, which is subtracted from sample titrations.',
            observe: [
              'The blank should require very little or no iodine before the endpoint appears',
              'Record the blank volume precisely — it is subtracted from all sample results',
              'The rapid titration flask gives an approximate endpoint to guide the slower titrations',
            ],
          }
        },
        {
          id: 6,
          title: 'Add Starch Indicator',
          shortDescription: 'Add 3 mL of starch test solution as endpoint is approached',
          handler: 'handleAddStarch',
          validation: {
            condition: 'nearEndpoint'
          },
          infoContent: {
            title: 'Adding Starch Near Endpoint',
            content: 'Add **3 mL of starch test solution** as the end point is approached. Adding starch early can cause iodine to bind irreversibly, making the endpoint hard to see. Adding starch near the endpoint gives a sharp colour change.',
            whatHappens: 'Starch indicator is added near (not before) the endpoint because it forms a reversible blue-black complex with free I2, giving a sharp visual signal when excess iodine first appears.',
            observe: [
              'Add exactly 3 mL of starch test solution when the solution begins to show a faint yellow tint',
              'Do not add starch too early — it can trap iodine irreversibly and obscure the endpoint',
              'The solution remains colourless after starch addition if vitamin C is still present',
            ],
          }
        },
        {
          id: 7,
          title: 'Titrate Dropwise to Endpoint',
          shortDescription: 'Continue DROPWISE until dirty purple color appears',
          handler: 'handleTitrateToEndpoint',
          validation: {
            requires: ['hasStarch'],
          },
          infoContent: {
            title: 'Starch-Iodine Complex',
            content: 'Continue adding I₂ dropwise. The final endpoint is a **"dirty purple"** that **persists for more than 90 seconds**. This is the first sign that all vitamin C has reacted.',
            chemicalEquation: 'I2 + Starch -> Blue-black complex',
            whatHappens: 'When all ascorbic acid has been oxidized, the next drop of iodine remains unreacted and forms a blue-black complex with starch, producing the characteristic "dirty purple" endpoint colour.',
            observe: [
              'Add iodine dropwise — one drop at a time near the endpoint',
              'The endpoint appears as a persistent "dirty purple" or blue-black colour',
              'The colour must persist for more than 90 seconds to be considered valid',
            ],
          }
        },
        {
          id: 8,
          title: 'Start 90-Second Timer',
          shortDescription: 'Color must persist for 90 seconds to be valid',
          handler: 'handleStartTimer',
          infoContent: {
            title: 'Why 90 Seconds?',
            content: 'The endpoint colour (dirty purple) must persist for more than **90 seconds** to be considered valid. If the colour fades, more ascorbic acid is present and being oxidized. A persistent colour means all Vitamin C has reacted. Repeat with the other samples.',
            whatHappens: 'A 90-second timer validates the endpoint; if the colour fades before 90 seconds, residual ascorbic acid is still reacting with the iodine and more must be added.',
            observe: [
              'Start the timer immediately when the dirty purple colour appears',
              'If the colour fades before 90 seconds, add another drop and restart timing',
              'A persistent colour confirms all vitamin C has been completely oxidized',
            ],
          }
        },
        {
          id: 9,
          title: 'Record Volume (if valid)',
          shortDescription: 'Record burette reading if color persisted',
          handler: 'handleRecordVolume',
          validation: {
            requires: ['colorPersisted']
          }
        },
        {
          id: 10,
          title: 'Repeat for Remaining Samples',
          shortDescription: 'Titrate samples 2, 3, and 4',
          handler: 'handleRepeatTitration'
        }
      ]
    },
    {
      id: 3,
      name: 'calculations',
      title: 'Part III: Calculations and USP Evaluation',
      equipment: [],
      initialState: {
        labelClaim: 500,
        calculatedValues: [],
        averagePerTablet: null,
        percentOfLabel: null,
        uspCompliant: null
      },
      steps: [
        {
          id: 1,
          title: 'Calculate mg Ascorbic Acid per Sample',
          shortDescription: 'Each mL of 0.1 N iodine = 8.806 mg C₆H₈O₆',
          handler: 'handleCalculateMgPerSample',
          infoContent: {
            title: 'Question 1 (1 mark)',
            content: 'Calculate the amount of ascorbic acid in each sample. Each mL of **0.1 N iodine** is equivalent to **8.806 mg of C₆H₈O₆**.',
            whatHappens: 'The volume of iodine consumed is multiplied by the conversion factor (8.806 mg/mL) to determine the mass of ascorbic acid in each sample.',
            observe: [
              'Subtract the blank volume from each sample titre before calculating',
              'mg ascorbic acid = (net mL I2) x 8.806 mg/mL',
              'Calculate for all three samples individually',
            ],
          }
        },
        {
          id: 2,
          title: 'Determine mg C₆H₈O₆ per Tablet',
          shortDescription: 'Scale using sample weight and average tablet weight; check USP guidelines',
          handler: 'handleCalculatePerTablet',
          validation: {
            requires: ['mgPerSampleCalculated']
          },
          infoContent: {
            title: 'Question 2 (2 marks)',
            content: 'Determine the amount of C₆H₈O₆ per tablet (you know how much ascorbic acid is in your sample, your sample weight and the tablet weight). Give an average. Determine if this result falls within USP guidelines (**90.0% to 110.0%** of labelled amount).',
            whatHappens: 'The mg of ascorbic acid per sample is scaled up to per-tablet amount using the ratio of average tablet mass to sample mass, then compared to the USP acceptance range.',
            observe: [
              'mg per tablet = (mg ascorbic acid in sample) x (avg tablet mass / sample mass)',
              'Average the three sample results for the final per-tablet value',
              'USP compliance requires the result to fall between 90% and 110% of the label claim',
            ],
          }
        },
        {
          id: 3,
          title: 'Serial Dilution Question',
          shortDescription: 'Describe procedure to make 250 mL of 0.0001 N H₂SO₄ from 18 M H₂SO₄',
          handler: 'handleSerialDilution',
          infoContent: {
            title: 'Question 3 (1 mark)',
            content: 'Describe the procedure that must be used to make **250 mL of 0.0001 N H₂SO₄** from **18 M H₂SO₄**. This is a **serial dilution** problem. Show all calculations.',
            whatHappens: 'A serial dilution calculation is performed because the concentration difference between 18 M and 0.0001 N is too large for a single accurate dilution step.',
            observe: [
              'The dilution factor is extremely large — a single dilution would require impossibly small volumes',
              'Two or more sequential dilution steps are required for accuracy',
              'Show all intermediate concentrations and volumes in the calculation',
            ],
          }
        },
        {
          id: 4,
          title: 'Accuracy Evaluation',
          shortDescription: 'Accuracy worth 3 marks',
          handler: 'handleEvaluateAccuracy',
          infoContent: {
            title: 'Question 4 — Accuracy (3 marks)',
            content: `Accuracy scoring:
- **+/- 10%** = 3 marks
- **+/- 12.5%** = 2 marks
- **+/- 15%** = 1 mark
- **+/- >15%** = 0 marks`,
            whatHappens: 'The percent difference between the experimental result and the labelled vitamin C content determines the accuracy score on a tiered scale.',
            observe: [
              'Calculate percent difference: |experimental - label| / label x 100%',
              'Within 10% of the label claim earns full accuracy marks',
              'Greater than 15% deviation earns zero accuracy marks',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp6',
    title: 'Vitamin C Analysis Quiz',
    questions: [
      {
        id: 'exp6-q1',
        type: 'mcq' as const,
        question: 'What type of reaction is used in this titration?',
        options: [
          { id: 'a', text: 'Acid-base neutralization' },
          { id: 'b', text: 'Complexometric' },
          { id: 'c', text: 'Iodometric redox' },
          { id: 'd', text: 'Precipitation' },
        ],
        correctOptionId: 'c',
        explanation: 'This is an iodometric redox titration: ascorbic acid + I₂ → dehydroascorbic acid + 2HI. Iodine oxidizes ascorbic acid.',
      },
      {
        id: 'exp6-q2',
        type: 'mcq' as const,
        question: 'What is the endpoint indicator and what color change is observed?',
        options: [
          { id: 'a', text: 'Phenolphthalein - colorless to pink' },
          { id: 'b', text: 'Starch - blue-black color that persists for >90 seconds' },
          { id: 'c', text: 'Methyl orange - yellow to red' },
          { id: 'd', text: 'Bromothymol blue - blue to yellow' },
        ],
        correctOptionId: 'b',
        explanation: 'Starch forms a blue-black complex with excess I₂. The endpoint is a "dirty purple" color that persists for more than 90 seconds.',
      },
      {
        id: 'exp6-q3',
        type: 'calculation' as const,
        question: 'If a vitamin C sample required 12.50 mL of 0.10 N I₂, how many mg of ascorbic acid (C₆H₈O₆) were present? (1 mL of 0.1 N I₂ = 8.806 mg C₆H₈O₆)',
        expectedValue: 110.08,
        tolerance: 2,
        unit: 'mg',
        explanation: 'mg = 12.50 mL × 8.806 mg/mL = 110.08 mg',
      },
      {
        id: 'exp6-q4',
        type: 'short_answer' as const,
        question: 'Why must oxygen-free (freshly boiled and cooled) water be used to dissolve the vitamin C sample?',
        sampleAnswer: 'Dissolved oxygen in water can oxidize ascorbic acid, reducing its amount before titration begins. Using freshly boiled water removes dissolved O₂, preventing premature oxidation and ensuring accurate determination of the vitamin C content.',
      },
      {
        id: 'exp6-q5',
        type: 'mcq' as const,
        question: 'USP standards require tablets to contain what percentage of the labeled amount?',
        options: [
          { id: 'a', text: '80-120%' },
          { id: 'b', text: '95-105%' },
          { id: 'c', text: '90-110%' },
          { id: 'd', text: '85-115%' },
        ],
        correctOptionId: 'c',
        explanation: 'USP (United States Pharmacopeia) standards require tablets to contain 90-110% of the labeled amount to be considered acceptable.',
      },
    ],
  },
};

export default experiment6Config;
